import { AppError } from "@/lib/utils/errors";
import { IStorageService } from "../storage";

export class GoogleCloudStorageClient implements IStorageService {
  private apiBaseUrl: string;
  private publicHost: string =
    process.env.PUBLIC_STORAGE_HOST ||
    "https://storage.googleapis.com/storage.dev.togetherwork.vn";

  constructor(apiBaseUrl?: string) {
    // Use the API route in Next.js to handle server-side GCP operations
    this.apiBaseUrl = apiBaseUrl || "/api/storage";
  }

  /**
   * Upload a file to the public bucket
   * Uses Next.js API route as proxy to GCP
   * @returns Public URL of the uploaded file
   */
  async uploadPublicFile(
    file: File | Blob,
    destination?: string,
  ): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);

    if (destination) {
      formData.append("destination", destination);
    } else {
      // Generate a path based on file name if not provided
      const fileName = (file as File).name || `file-${Date.now()}`;
      formData.append("destination", `uploads/${fileName}`);
    }

    formData.append("bucket", "public");

    const response = await fetch(`${this.apiBaseUrl}/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload a file to the private bucket
   * Uses Next.js API route as proxy to GCP
   * @returns File path in the private bucket
   */
  async uploadPrivateFile(
    file: File | Blob,
    destination?: string,
  ): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);

    if (destination) {
      formData.append("destination", destination);
    } else {
      // Generate a path based on file name if not provided
      const fileName = (file as File).name || `file-${Date.now()}`;
      formData.append("destination", `uploads/${fileName}`);
    }

    formData.append("bucket", "private");

    const response = await fetch(`${this.apiBaseUrl}/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const data = await response.json();
    return data.path;
  }

  /**
   * Get the public URL for a file
   */
  getPublicFileUrl(filePath: string): string {
    return `${this.publicHost}${filePath}`;
  }

  /**
   * Generate a signed URL for temporary access to a private file
   * Uses Next.js API route as proxy to GCP
   */
  async getPrivateFileSignedUrl(
    filePath: string,
    expiryMinutes: number = 15,
  ): Promise<string> {
    const response = await fetch(`${this.apiBaseUrl}/signedUrl`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filePath,
        expiryMinutes,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Download a file from the private bucket
   * Uses Next.js API route as proxy to GCP
   */
  async downloadPrivateFile(filePath: string): Promise<Blob> {
    const response = await fetch(`${this.apiBaseUrl}/download`, {
      method: "POST",
      cache: "force-cache",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filePath }),
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    return await response.blob();
  }

  /**
   * Delete a file from either bucket
   * Uses Next.js API route as proxy to GCP
   */
  async deleteFile(
    filePath: string,
    isPublic: boolean = false,
  ): Promise<boolean> {
    const response = await fetch(`${this.apiBaseUrl}/delete`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filePath,
        bucket: isPublic ? "public" : "private",
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      const appErr = new AppError(error);
      appErr.logError();
      return false;
    }

    return true;
  }

  /**
   * Create a blob URL for client-side use
   */
  static createBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  /**
   * Generate a unique filename with timestamp
   */
  static generateUniqueFilename(originalFilename: string): string {
    const lastDot = originalFilename.lastIndexOf(".");
    const ext = lastDot !== -1 ? originalFilename.slice(lastDot) : "";
    const name =
      lastDot !== -1 ? originalFilename.slice(0, lastDot) : originalFilename;
    const timestamp = Date.now();
    return `${name}-${timestamp}${ext}`;
  }
}

// Factory function to create the storage service
export function createStorageService(apiBaseUrl?: string): IStorageService {
  return new GoogleCloudStorageClient(apiBaseUrl);
}
